import React from 'react'
import styled from '@emotion/styled'
import type { BetSource } from '@mobi/betslip/types'
import { Proposition } from '@core/Areas/RaceCardFixedOnly/Data/types'
import type { RawTips } from '@core/Areas/Racing/Components/BettableTips/types'
import { RaceCodesLowerCase } from '../../Types/MeetingInformation'
import type { PreviewTipsTrackerEvent } from '../RaceContent/helpers/analytics'
import { BettableTipFixedOnly } from './BettableTipFixedOnly'

export type Props = {
  tips: RawTips
  isRaceOpen: boolean
  tipSource: BetSource
  propositions?: Proposition[]
  onPropositionClick?: (proposition: Proposition, betSource?: BetSource) => void
  isFieldSummaryPopulated?: boolean
  meetingCode?: RaceCodesLowerCase
  racecourseSeq?: number
  meetingDate?: string
  raceNumber?: number
  acceptorsLimit?: number
  raceStatus?: PreviewTipsTrackerEvent['raceStatus']
  meetingName?: string
}
export const BettableTipsFixedOnly = ({
  tips,
  isRaceOpen,
  tipSource,
  propositions,
  onPropositionClick,
  isFieldSummaryPopulated,
  meetingCode,
  racecourseSeq,
  meetingDate,
  raceNumber,
  acceptorsLimit,
  raceStatus,
  meetingName,
}: Props) => {
  const tippedPropositions = mapTipsToPropositions(propositions, tips)
  if (tippedPropositions.length < 1) {
    return null
  }

  const displayedPropositions = acceptorsLimit
    ? tippedPropositions.slice(0, acceptorsLimit)
    : tippedPropositions

  return (
    <WrapperStyled data-testid={tipSource === 'tips' ? 'bettable-tips' : 'preview-tips'}>
      {displayedPropositions.map((proposition, i) => (
        <BettableTipFixedOnly
          key={`${proposition.starterNumber}-${i}`}
          proposition={proposition}
          buttonPosition={i + 1}
          isRaceOpen={isRaceOpen}
          onClick={selectedProposition => {
            onPropositionClick?.(selectedProposition, tipSource)
          }}
          isFieldSummaryPopulated={isFieldSummaryPopulated}
          meetingCode={meetingCode}
          racecourseSeq={racecourseSeq}
          meetingDate={meetingDate}
          raceNumber={raceNumber}
          raceStatus={raceStatus}
          meetingName={meetingName}
        />
      ))}
    </WrapperStyled>
  )
}

// Local Helpers
const mapTipsToPropositions = <T extends { starterNumber?: number }>(
  propositions: T[] | undefined,
  tips: RawTips | undefined
): T[] => {
  if (!propositions?.length || !tips) return []

  const numericTips = Array.isArray(tips)
    ? tips.filter((tip): tip is number => typeof tip === 'number')
    : []

  return numericTips
    .map(tip => propositions.find(proposition => proposition.starterNumber === tip))
    .filter((proposition): proposition is T => Boolean(proposition))
}

// Styles
export const WrapperStyled = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
  columnGap: '0.5rem',
})
